import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useMemo,
} from "react";

interface DeveloperModeContextType {
  developerMode: boolean;
  toggleDeveloperMode: () => void;
  signatureOverrideActive: boolean;
  setSignatureOverrideActive: (active: boolean) => void;
}

const DeveloperModeContext = createContext<DeveloperModeContextType>({
  developerMode: false,
  toggleDeveloperMode: () => {},
  signatureOverrideActive: false,
  setSignatureOverrideActive: () => {},
});

interface DeveloperModeProviderProps {
  children: ReactNode;
}

export const DeveloperModeProvider: React.FC<DeveloperModeProviderProps> = ({
  children,
}) => {
  const [developerMode, setDeveloperMode] = useState(false);
  const [signatureOverrideActive, setSignatureOverrideActive] = useState(false);

  const toggleDeveloperMode = () => {
    setDeveloperMode((prevMode) => {
      const next = !prevMode;
      if (!next) {
        setSignatureOverrideActive(false);
      }
      return next;
    });
  };

  const value = useMemo(
    () => ({
      developerMode,
      toggleDeveloperMode,
      signatureOverrideActive,
      setSignatureOverrideActive,
    }),
    [developerMode, signatureOverrideActive]
  );

  return (
    <DeveloperModeContext.Provider value={value}>
      {children}
    </DeveloperModeContext.Provider>
  );
};

export const useDeveloperMode = () => {
  const context = useContext(DeveloperModeContext);
  if (!context) {
    throw new Error(
      "useDeveloperMode must be used within a DeveloperModeProvider"
    );
  }
  return context;
};
